import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { accountService, UserProfile } from '../../../services/accountService';
import ProfileImageUpload from './ProfileImageUpload';
import ProfileForm from './ProfileForm';
import PasswordChangeForm from './PasswordChangeForm';

const AccountSettingsPage: React.FC = () => {
  const uiData = useSelector((state: RootState) => state.ui);
  const userData = useSelector((state: RootState) => state.user);
  const [profileData, setProfileData] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await accountService.getProfile();
      if (result.success && result.data) {
        setProfileData(result.data);
      } else {
        setError(result.error || 'Failed to load profile');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (imageUrl: string) => {
    if (profileData) {
      setProfileData({ ...profileData, profileImageUrl: imageUrl });
    }
  };

  const handleImageDelete = () => {
    if (profileData) {
      setProfileData({ ...profileData, profileImageUrl: null });
    }
  };

  const handleProfileSave = (firstName: string, lastName: string) => {
    if (profileData) {
      setProfileData({
        ...profileData,
        playerFirstName: firstName,
        playerLastName: lastName
      });
    }
  };

  const handlePasswordChanged = () => {
    // Password change successful - could show notification here
  };

  const getUserInitials = () => {
    if (!profileData) return '??';
    const first = profileData.playerFirstName?.[0] || '';
    const last = profileData.playerLastName?.[0] || '';
    return (first + last).toUpperCase() || '??';
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: 'rgba(255, 255, 255, 0.9)',
        fontSize: '2vh',
        fontFamily: 'Noto Sans'
      }}>
        Loading account settings...
      </div>
    );
  }

  if (error || !profileData) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '2vh'
      }}>
        <div style={{
          color: 'rgb(255, 150, 150)',
          fontSize: '2vh',
          fontFamily: 'Noto Sans'
        }}>
          {error || 'Failed to load profile'}
        </div>
        <button
          onClick={loadProfile}
          style={{
            backgroundColor: `rgba(${uiData.schemeColor}, 0.2)`,
            border: `0.2vh solid rgb(${uiData.schemeColor})`,
            padding: '1vh 2vh',
            fontSize: '1.5vh',
            fontFamily: 'Noto Sans',
            fontWeight: '600',
            borderRadius: '0.75vh',
            cursor: 'pointer',
            color: 'rgba(255, 255, 255, 0.9)'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{
      width: '100%',
      height: '100%',
      overflow: 'auto',
      boxSizing: 'border-box',
      position: 'relative',
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      flexDirection: 'column',
      textAlign: 'left',
      gap: '3vh',
      padding: '3vh 0',
      color: 'rgba(255, 255, 255, 0.9)'
    }}>
      {/* Page Title */}
      <div style={{
        fontSize: '6vh',
        fontFamily: 'Oswald',
        fontWeight: '500'
      }}>
        Account Settings
      </div>

      {/* Profile Section */}
      <div style={{
        width: '100%',
        backgroundColor: 'rgba(60, 60, 60, 0.3)',
        borderRadius: '1vh',
        padding: '3vh',
        boxSizing: 'border-box',
        display: 'flex',
        gap: '4vh'
      }}>
        <div style={{
          minWidth: '20vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start'
        }}>
          <ProfileImageUpload
            currentImageUrl={profileData.profileImageUrl}
            userInitials={getUserInitials()}
            onImageChange={handleImageChange}
            onImageDelete={handleImageDelete}
          />
        </div>

        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: '2vh'
        }}>
          <div style={{
            fontSize: '3vh',
            fontFamily: 'Oswald',
            fontWeight: '500'
          }}>
            PROFILE
          </div>
          <ProfileForm
            userData={profileData}
            onSave={handleProfileSave}
          />
        </div>
      </div>

      {/* Password Section - Only for email/password users */}
      {profileData.provider === 'email' && (
        <div style={{
          width: '100%',
          backgroundColor: 'rgba(60, 60, 60, 0.3)',
          borderRadius: '1vh',
          padding: '3vh',
          boxSizing: 'border-box'
        }}>
          <div style={{
            fontSize: '3vh',
            fontFamily: 'Oswald',
            fontWeight: '500',
            marginBottom: '2vh'
          }}>
            PASSWORD
          </div>
          <div style={{ maxWidth: '50vh' }}>
            <PasswordChangeForm onPasswordChanged={handlePasswordChanged} />
          </div>
        </div>
      )}

      {/* Account Info Section */}
      <div style={{
        width: '100%',
        backgroundColor: 'rgba(60, 60, 60, 0.3)',
        borderRadius: '1vh',
        padding: '3vh',
        boxSizing: 'border-box'
      }}>
        <div style={{
          fontSize: '3vh',
          fontFamily: 'Oswald',
          fontWeight: '500',
          marginBottom: '2vh'
        }}>
          ACCOUNT INFORMATION
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5vh',
          fontFamily: 'Noto Sans',
          fontSize: '1.5vh'
        }}>
          <div style={{ display: 'flex', gap: '1vh' }}>
            <span style={{ color: 'rgba(255, 255, 255, 0.6)', minWidth: '15vh' }}>
              Login Method:
            </span>
            <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: '600' }}>
              {profileData.provider === 'email' ? 'Email/Password' : profileData.provider}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '1vh' }}>
            <span style={{ color: 'rgba(255, 255, 255, 0.6)', minWidth: '15vh' }}>
              Account Created:
            </span>
            <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: '600' }}>
              {formatDate(profileData.createdAt)}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '1vh' }}>
            <span style={{ color: 'rgba(255, 255, 255, 0.6)', minWidth: '15vh' }}>
              User ID:
            </span>
            <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: '600' }}>
              {profileData.UserStateID}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSettingsPage;
